import { Alert, Button, Container, Loader, Stack, Text } from "@mantine/core";
import type { ReactElement, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import { useMe } from "../features/auth/useMe";
import { removeOrgScopedQueries } from "../query/scope";
import { useAuthStore } from "./store";

export interface RequireActiveOrgProps {
  readonly children: ReactNode;
}

/**
 * Гейт между `<ProtectedRoute>` и `<AppLayout>` — одна дверь на всю
 * защищённую ветку роутов (ADR-0038, фаза 2).
 *
 * Источник истины об активной org сессии — сервер (`GET
 * /me.active_org_id`); `identity.orgId` в localStorage — лишь UI-кэш
 * последнего известного значения (например, хинт от старого
 * register-ответа или org, обнулённая на бэке после отзыва членства).
 * Эффект синхронизации стора работает независимо от того, какая ветка
 * рендерится — в том числе на быстром пути. Он же чистит кэш: ответы,
 * пришедшие пока стор расходился с сервером, атрибутированы прежней
 * организацией (ключ строится из `identity.orgId`, см.
 * `query/scope.ts`), и жить им нельзя. Момент срабатывания задаёт
 * `/me`: гейт смонтирован один раз на всю защищённую ветку, так что
 * сверка происходит на перезагрузке вкладки, восстановлении связи или
 * появлении нового потребителя `/me`, а не на каждом переходе.
 *
 * Недоверие к кэшу `/me`: если стор сам перешёл с org на `null`
 * (`authedFetch` получил 401 `org_context_required` и сбросил
 * организацию), а кэш `/me` всё ещё называет прежнюю org, кэшу не
 * верим. Сразу вернуть её в стор нельзя: org-запросы повторились бы,
 * снова получили бы 401 — бесконечный цикл. Вместо этого гейт
 * перезапрашивает `/me` и синхронизирует стор только по свежему
 * ответу: `null` → `/select-org`, org → стор возвращается к ней
 * (решение сервера) без сноса её кэша. Сервер, противоречащий сам
 * себе (`/me` = X, а org-ручка стабильно `org_context_required`),
 * зациклил бы гейт со скоростью сети; на бэке оба ответа читают один
 * `principal.org_id`, поэтому случай недостижим. На первой загрузке (стор пуст, `/me` называет
 * org) синхронизация прежняя — сразу.
 *
 * Быстрый путь: при непустом `identity.orgId` children рендерятся
 * СРАЗУ, не дожидаясь `/me` — иначе каждая защищённая страница
 * получала бы лишний блокирующий round-trip. `useMe` гоняется с тем
 * же `queryKey`, что и в `AppLayout` — TanStack Query дедуплицирует
 * запрос, лишнего обращения к бэку нет.
 */
export function RequireActiveOrg({
  children,
}: RequireActiveOrgProps): ReactElement {
  const { t } = useTranslation();
  const identity = useAuthStore((state) => state.identity);
  const activeOrgId = identity?.orgId ?? null;
  const location = useLocation();
  const me = useMe();
  const queryClient = useQueryClient();

  const { refetch } = me;
  const previousOrgId = useRef(activeOrgId);
  // Org до сброса по `org_context_required`: если свежий `/me` её
  // подтвердит, кэш принадлежит той же организации и сносить его незачем.
  const orgBeforeReset = useRef<string | null>(null);

  useEffect(() => {
    const previous = previousOrgId.current;
    previousOrgId.current = activeOrgId;
    if (me.data === undefined) {
      return;
    }
    if (me.data.active_org_id === activeOrgId) {
      return;
    }
    if (previous !== null && activeOrgId === null) {
      // Стор обнулили, а кэш помнит прежнюю org: ждём свежий `/me`.
      orgBeforeReset.current = previous;
      void refetch();
      return;
    }
    const restored =
      orgBeforeReset.current !== null &&
      me.data.active_org_id === orgBeforeReset.current;
    orgBeforeReset.current = null;
    if (restored) {
      // Сервер подтвердил ту же org: кэш её, возвращаем только стор.
      useAuthStore.getState().setActiveOrg(me.data.active_org_id);
      return;
    }
    // Стор врал: организацию запроса выбирает сервер по cookie, а ключ
    // кэша строился из `identity.orgId` (протухший localStorage, смена
    // организации в соседней вкладке). Значит всё, что успел набрать
    // быстрый путь, лежит под ключом ЧУЖОЙ организации и переживёт
    // смену ключа на `gcTime`. Сносим org-scoped записи целиком —
    // иначе возврат в прежнюю организацию покажет их синхронно.
    removeOrgScopedQueries(queryClient);
    useAuthStore.getState().setActiveOrg(me.data.active_org_id);
    // `dataUpdatedAt` в зависимостях: свежий ответ с тем же телом
    // сохраняет ссылку `me.data` (structural sharing), но обязан
    // перезапустить сверку.
  }, [me.data, me.dataUpdatedAt, activeOrgId, queryClient, refetch]);

  if (activeOrgId !== null) {
    // Быстрый путь: не ждём /me. Эффект выше всё равно синхронизирует
    // стор, если сервер вдруг скажет иное (протухший localStorage) —
    // следующий рендер уйдёт в Navigate.
    return <>{children}</>;
  }

  if (me.isPending) {
    return (
      <Container py="xl">
        <Stack gap="md" align="center">
          <Loader />
          <Text>{t("orgSelect.checking")}</Text>
        </Stack>
      </Container>
    );
  }

  if (me.isError) {
    return (
      <Container py="xl">
        <Alert color="red" title={t("orgSelect.errorHeading")}>
          <Stack gap="sm">
            <Text>{t("orgSelect.checkError")}</Text>
            <Button
              variant="light"
              onClick={() => {
                void me.refetch();
              }}
            >
              {t("orgSelect.retry")}
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }

  if (me.data.active_org_id !== null) {
    // Сервер знает org, стор ещё нет — эффект выше синхронизирует
    // стор, следующий рендер уйдёт в быстрый путь. Пока — лоадер без
    // текста-«проверяем»: это переходное состояние одного тика, а не
    // ожидание сетевого запроса.
    return (
      <Container py="xl">
        <Stack gap="md" align="center">
          <Loader />
        </Stack>
      </Container>
    );
  }

  return (
    <Navigate to="/select-org" replace state={{ from: location.pathname }} />
  );
}
